import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Loader2, Gift } from 'lucide-react';
import { ReferralTermsContent, ReferralTermsLink } from './ReferralRules';
import { TERMS_VERSION } from './referralTermsText';

/**
 * WO-71 — Ambassador / First Fifty invitation (Referral Programme Terms v1.0, BILAL
 * 23/10/2026). When the admin invites someone, this window appears the next time they
 * open the app. Their free Premium starts the moment they accept (clauses A2 / F2);
 * the acceptance is recorded with the Terms version and the date. "Not now" closes it
 * for this visit; it comes back next time. Friendly tone, by Lucas's request.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LATER_KEY = 'm2b_promo_invite_later';

export default function PromoInvitation() {
  const { user, token } = useAuth();
  const location = useLocation();
  const [inv, setInv] = useState(null);
  const [ticked, setTicked] = useState(false);
  const [showSection, setShowSection] = useState(false);
  const [busy, setBusy] = useState(false);
  const inApp = location.pathname.startsWith('/dashboard');

  useEffect(() => {
    let cancelled = false;
    if (!user || !token || !inApp) return undefined;
    try { if (sessionStorage.getItem(LATER_KEY) === '1') return undefined; } catch (e) { /* ignore */ }
    fetch(`${API_URL}/api/referral/invitation`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && d.invitation) setInv(d.invitation); })
      .catch(() => { /* try again next visit */ });
    return () => { cancelled = true; };
  }, [user, token, inApp]);

  if (!inv) return null;
  const amb = inv.kind === 'ambassador';

  const later = () => {
    try { sessionStorage.setItem(LATER_KEY, '1'); } catch (e) { /* ignore */ }
    setInv(null);
  };

  const accept = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/referral/invitation/accept`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: TERMS_VERSION }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'error');
      toast.success(d.message || 'Welcome!', { duration: 8000 });
      setInv(null);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.message || 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!inv} onOpenChange={(o) => { if (!o) later(); }}>
      <DialogContent className="sm:max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="promo-invitation" dir="ltr">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-emerald-500" />
            {amb ? 'You’re invited to be a Mid2Bid Ambassador' : 'Welcome to Mid2Bid First Fifty'}
          </DialogTitle>
          <DialogDescription>
            {amb
              ? 'A small thank-you for being with us from the very start.'
              : 'A personal trial, so you can see everything Mid2Bid can do.'}
          </DialogDescription>
        </DialogHeader>
        <ul className="text-sm space-y-2 list-disc ps-5">
          {amb ? (
            <>
              <li><strong>Premium free for 12 months</strong> — no card, and nothing you have to do in return.</li>
              <li>If you like Mid2Bid, share your personal link with people you know. They get 15% off, and you earn 15% of what they pay.</li>
              <li>Your free Premium renews every year you bring in more than 100 new paying users.</li>
              <li>Just one thing: share your link personally, not in public posts.</li>
            </>
          ) : (
            <>
              <li><strong>Premium free for 90 days</strong> — no card, and nothing you have to do in return.</li>
              <li>At the end your account simply goes back to its plan. Nothing is ever charged.</li>
              <li>We will remind you before the 90 days are up.</li>
            </>
          )}
        </ul>
        <button type="button" className="text-xs underline text-muted-foreground text-start" onClick={() => setShowSection(!showSection)}
          data-testid="promo-show-section">
          {showSection ? 'Hide' : 'Read'} the {amb ? 'Ambassador section' : 'First Fifty conditions'}
        </button>
        {showSection && (
          <div className="rounded-md border p-3 max-h-60 overflow-y-auto">
            <ReferralTermsContent only={amb ? 'ambassador' : 'first_fifty'} />
          </div>
        )}
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="mt-1" checked={ticked} onChange={(e) => setTicked(e.target.checked)} data-testid="promo-accept-tick" />
          <span>
            {amb
              ? <>I accept the <ReferralTermsLink isAr={false} label={`Referral Programme Terms (version ${TERMS_VERSION})`} />, including the Ambassador section.</>
              : <>I accept the First Fifty conditions (section F of the <ReferralTermsLink isAr={false} label={`Referral Programme Terms, version ${TERMS_VERSION}`} />).</>}
          </span>
        </label>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={later} data-testid="promo-later">Not now</Button>
          <Button onClick={accept} disabled={!ticked || busy} data-testid="promo-accept">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Start my free Premium
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
